import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { workbooksApi } from "../api/workbooks";
import { catalogueApi, bundleSlugForRoute } from "../api/catalogue";
import { workbooksForBundle, isBundleOwned } from "../data/workbookBundles";
import Navbar from "../components/Navbar";
import { WorkbookCard } from "../components/WorkbookCard";
import WorkbookBreadcrumb from "../components/WorkbookBreadcrumb";
import WorkbookReviews from "../components/layout/WorkbookReviews";
import LoadErrorPanel from "../components/LoadErrorPanel";
import {
  settled,
  isAnswered,
  anyFailed,
  LOADING_STATE,
} from "../lib/fetchState";

/*
  Per bundle library. Route: /workbooks/bundle/:track/library
  Owners only. Shows the track's workbooks as old style cards with progress.
  Non owners and unknown tracks are redirected to the sales detail page.
  No backend changes. No em or en dashes.
*/

// Clamp a description to a short snippet at a word boundary so the note box
// stays about two lines, matching the old library. Appends "..." (three dots)
// only when the text was actually shortened.
const LIMIT = 90;
function clampNote(text) {
  const s = (text || "").trim();
  if (s.length <= LIMIT) return s;
  const cut = s.slice(0, LIMIT);
  const lastSpace = cut.lastIndexOf(" ");
  const head = (lastSpace > 0 ? cut.slice(0, lastSpace) : cut).replace(/[\s.,;:]+$/, "");
  return head + "...";
}

// The title, accent and membership rule used to be a fourth hardcoded copy of
// the bundle config, sitting here as TRACK. When it disagreed with the catalog's
// copy, a buyer was bounced out of the library they had just paid for. It all
// comes from /api/bundles now.

export default function BundleLibrary() {
  const { track } = useParams();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [workbooks, setWorkbooks] = useState([]);
  const [ownedBundleSlugs, setOwnedBundleSlugs] = useState([]);
  const [loading, setLoading] = useState(true);
  // Workbook id whose reviews modal is open, or null.
  const [reviewsId, setReviewsId] = useState(null);
  // How each read went, apart from what it returned. This page ejects non-owners
  // to the sales page, so a failed read used to throw an owner out of a library
  // they had paid for on nothing more than a dropped request.
  const [ownershipState, setOwnershipState] = useState(LOADING_STATE);
  const [bundleState, setBundleState] = useState(LOADING_STATE);

  const aliveRef = useRef(true);
  // Set once the guard below has ejected someone, so it fires at most once per
  // track. Declared up here because the loader resets it.
  const redirectedRef = useRef(false);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    return Promise.all([
      settled(workbooksApi.getAll()),
      settled(catalogueApi.getBundle(bundleSlugForRoute(track))),
    ]).then(([ownership, bundle]) => {
      if (!aliveRef.current) return;
      setOwnershipState(ownership);
      setBundleState(bundle);
      if (isAnswered(ownership)) {
        setWorkbooks(ownership.data?.workbooks || []);
        setOwnedBundleSlugs(ownership.data?.ownedBundleSlugs || []);
      }
      if (isAnswered(bundle)) setCfg(bundle.data || null);
      setLoading(false);
    });
  }, [track]);

  useEffect(() => {
    setOwnershipState(LOADING_STATE);
    setBundleState(LOADING_STATE);
    // A fresh track is a fresh verdict: let the guard below fire again.
    redirectedRef.current = false;
    load();
  }, [load]);

  // One ownership helper, shared with the catalog and the detail page, so the
  // three can no longer disagree and redirect a paying user in a circle.
  //
  // Gated on the read having answered: an unanswered read is not a verdict, and
  // acting on it is what ejected owners from their own library.
  const owned =
    isAnswered(ownershipState) && isBundleOwned(cfg, ownedBundleSlugs);

  // Guard: non owners (and unknown tracks) go to the sales detail page.
  //
  // Fired once, like the matching guard on the detail page. Without the ref a
  // re-render while the redirect is in flight fires it again, and the two pages
  // guard each other, so that is one navigation away from a loop.
  useEffect(() => {
    if (loading || redirectedRef.current) return;
    // Only an authoritative pair of answers may eject anyone. If either read
    // failed, the error panel below takes over instead.
    if (!isAnswered(ownershipState) || !isAnswered(bundleState)) return;
    if (!cfg || !owned) {
      redirectedRef.current = true;
      navigate(`/workbooks/bundle/${track}`, { replace: true });
    }
  }, [loading, cfg, owned, ownershipState, bundleState, track, navigate]);

  if (loading) {
    return (
      <>
        <Navbar />
        <div
          style={{
            background: "#f5f0e8",
            minHeight: "100vh",
            display: "grid",
            placeItems: "center",
            color: "#9a8f7e",
            fontFamily: "'Inter', sans-serif",
          }}
        >
          Loading...
        </div>
      </>
    );
  }

  // A read that did not come back. Show it, rather than treating it as a verdict
  // about what this visitor owns.
  if (anyFailed(ownershipState, bundleState)) {
    return (
      <>
        <Navbar />
        <LoadErrorPanel
          title="We could not load your library"
          message="Something went wrong reaching the server, so we cannot confirm what you own. Nothing has changed in your account."
          onRetry={load}
          retrying={loading}
          background="#f5f0e8"
          ink="#1a1a1a"
          muted="#7a6e60"
        />
      </>
    );
  }

  // While the guard effect redirects, render nothing.
  if (!cfg || !owned) return null;

  const trackWbs = workbooksForBundle(cfg, workbooks);

  const total = trackWbs.length;
  const completed = trackWbs.filter((w) => w.isComplete).length;

  return (
    <div style={{ background: "#f5f0e8", minHeight: "100vh", fontFamily: "'Inter', sans-serif" }}>
      <Navbar />
      <div style={{ maxWidth: 1300, margin: "0 auto", padding: "3.5rem 1.5rem" }}>
        <WorkbookBreadcrumb
          items={[{ label: "Workbooks", to: "/workbooks" }, { label: cfg.title }]}
          backTo="/workbooks"
          backLabel="Back to library"
        />
        <div style={{ marginBottom: "2rem", marginTop: "1.25rem" }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: ".14em",
              textTransform: "uppercase",
              color: "#9a8f7e",
              marginBottom: ".85rem",
            }}
          >
            Your library
          </div>
          <h1
            style={{
              fontFamily: "'Fraunces', serif",
              fontSize: 34,
              fontWeight: 700,
              color: "#1a1a1a",
              lineHeight: 1.1,
              marginBottom: ".6rem",
            }}
          >
            {cfg.title}
          </h1>
          <p style={{ fontSize: 14, color: "#7a6e60" }}>
            {completed} of {total} workbooks complete
          </p>
        </div>

        {total === 0 ? (
          <div
            style={{
              border: "1.5px dashed #ddd4c2",
              borderRadius: 8,
              padding: "2.5rem",
              textAlign: "center",
              color: "#9a8f7e",
              fontSize: 14,
            }}
          >
            No workbooks here yet.
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: 12,
            }}
          >
            {trackWbs.map((wb, i) => (
              <WorkbookCard
                key={wb.id}
                wb={wb}
                chLabel={`Vol.${i + 1}`}
                note={wb.description ? { icon: "📑", text: clampNote(wb.description) } : undefined}
                noteStyle="amber"
                accentColor={cfg.accent}
                locked={false}
                rating={
                  wb.avgRating != null
                    ? { avgRating: wb.avgRating, totalRatings: wb.ratingCount }
                    : undefined
                }
                onShowReviews={() => setReviewsId(wb.id)}
              />
            ))}
          </div>
        )}
      </div>

      {reviewsId && (
        <>
          <div
            onClick={() => setReviewsId(null)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(15,15,20,0.45)",
              backdropFilter: "blur(3px)",
              zIndex: 1000,
            }}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 1001,
              width: "min(92vw, 560px)",
              maxHeight: "80vh",
              overflowY: "auto",
              background: "#F5F2EC",
              borderRadius: 16,
              padding: "2rem",
              boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
              fontFamily: "inherit",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "1.5rem",
              }}
            >
              <h3
                style={{
                  fontFamily: "'Fraunces', serif",
                  fontSize: 20,
                  fontWeight: 700,
                  color: "#1a1a1a",
                  margin: 0,
                }}
              >
                What students say
              </h3>
              <button
                onClick={() => setReviewsId(null)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#a1a1aa",
                  fontSize: 22,
                  padding: "0 0 0 8px",
                  lineHeight: 1,
                }}
              >
                ✕
              </button>
            </div>
            <WorkbookReviews workbookId={reviewsId} />
          </div>
        </>
      )}
    </div>
  );
}
